import React, { useContext } from 'react'
import { Switch, Route, Redirect, useParams } from 'react-router-dom'
import { Location, History } from 'history'
import BarcodesReportPage from 'reports/barcodes'
import DiscountsReportPage from 'reports/discounts'
import SalesReportPage from 'reports/sales'
import TrafficReportsPage from 'reports/traffic'
import EmailsReportsPage from 'reports/emails'
import EngageToolsStatsPage from 'engageTools/stats/index'
import { Feature, useHasFeature, withFeatures } from 'features'
import { StatsService } from 'http/statsService'
import { EngageToolsService } from 'engageTools/engageToolsService'
import { RequestService } from 'requests/requestService'
import { LoginService } from 'http/loginService'
import { LoggingService } from 'http/loggingService'
import { InventoryServiceContext } from 'inventory/context'
import CheckoutFunnelReports from './checkoutFunnel'
import Superset from './superset'
import { BaseRouteParams } from 'hocs'
import { SigmaReport, SigmaService } from './sigma'
import { useTranslation } from 'translations'

interface ReportsHomeProps {
    history: History
    location: Location
    statsService: StatsService
    engageToolsService: EngageToolsService
    requestService: RequestService
    backofficeEndpoint: string
    loginService: LoginService
    loggingService: LoggingService
    sigmaService: SigmaService
    hasFeature: (feature: string, accountSlug?: string) => boolean
}

function ReportsHome(props: ReportsHomeProps) {
    const inventoryService = useContext(InventoryServiceContext)
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const reportsHomePage = `/account/${accountSlug}/reports/${useHasFeature('ReportsOverview') ? 'overview' : 'sales'}`

    return (
        <Switch location={props.location}>
            <Route path="/account/:accountSlug/reports/superset/:dashboardId" render={() => <Superset />} />
            <Route
                path="/account/:accountSlug/reports/sales"
                render={() => (
                    <Feature name="SalesAndFinanceReport" accountSlug={accountSlug}>
                        <SigmaReport
                            sigmaService={props.sigmaService}
                            accountSlug={accountSlug}
                            reportId="14eNWRn8HsWBk7f7Yp5C92"
                            title={t('reports.titles.salesAndFinance')}
                        />
                    </Feature>
                )}
            />
            <Route
                path="/account/:accountSlug/reports/sales-demo"
                render={() => (
                    <Feature name="SalesAndFinanceReportDemo" accountSlug={accountSlug}>
                        <SigmaReport
                            sigmaService={props.sigmaService}
                            accountSlug={accountSlug}
                            reportId="5LPOIjzQXvASosUqkgIX5t"
                            title={t('reports.titles.salesAndFinanceDemo')}
                        />
                    </Feature>
                )}
            />
            <Route
                path="/account/:accountSlug/reports/operations"
                render={() => (
                    <Feature name="OperationsReport" accountSlug={accountSlug}>
                        <SigmaReport
                            sigmaService={props.sigmaService}
                            accountSlug={accountSlug}
                            reportId="5XDFrXuwUfFdjzn3CUcoml"
                            title={t('reports.titles.operations')}
                        />
                    </Feature>
                )}
            />
            <Route
                path="/account/:accountSlug/reports/marketing"
                render={() => (
                    <Feature name="MarketingReport" accountSlug={accountSlug}>
                        <SigmaReport
                            sigmaService={props.sigmaService}
                            accountSlug={accountSlug}
                            reportId="5mi0s5fSLcKF1mK1KEzcss"
                            title={t('reports.titles.marketing')}
                        />
                    </Feature>
                )}
            />
            <Route
                path="/account/:accountSlug/reports/sales-old"
                render={() => (
                    <Feature name="sale_reports" accountSlug={accountSlug}>
                        <SalesReportPage
                            statsService={props.statsService}
                            loginService={props.loginService}
                            loggingService={props.loggingService}
                            accountSlug={accountSlug}
                        />
                    </Feature>
                )}
            />
            <Route
                path="/account/:accountSlug/reports/barcodes"
                render={() => (
                    <Feature name="BarcodesReport" accountSlug={accountSlug}>
                        <BarcodesReportPage
                            statsService={props.statsService}
                            accountSlug={accountSlug}
                            inventoryService={inventoryService}
                        />
                    </Feature>
                )}
            />
            <Route
                path="/account/:accountSlug/reports/discount_codes"
                render={() => (
                    <Feature name="DiscountCodesReport" accountSlug={accountSlug}>
                        <DiscountsReportPage
                            statsService={props.statsService}
                            backofficeEndpoint={props.backofficeEndpoint}
                            loginService={props.loginService}
                            loggingService={props.loggingService}
                            accountSlug={accountSlug}
                        />
                    </Feature>
                )}
            />
            <Route
                path="/account/:accountSlug/reports/traffic"
                render={() => (
                    <Feature name="TrafficReport" accountSlug={accountSlug}>
                        <TrafficReportsPage accountSlug={accountSlug} />
                    </Feature>
                )}
            />
            <Route
                path="/account/:accountSlug/reports/emails"
                render={() => (
                    <Feature name="EmailsReport" accountSlug={accountSlug}>
                        <EmailsReportsPage accountSlug={accountSlug} />
                    </Feature>
                )}
            />
            <Route
                path="/account/:accountSlug/reports/engage_tools"
                render={() => (
                    <Feature name="EngageToolsReport" accountSlug={accountSlug}>
                        <EngageToolsStatsPage accountSlug={accountSlug} />
                    </Feature>
                )}
            />
            <Route
                path="/account/:accountSlug/reports/checkout_funnel"
                render={() => (
                    <Feature name="CheckoutFunnelReport" accountSlug={accountSlug}>
                        <CheckoutFunnelReports accountSlug={accountSlug} />
                    </Feature>
                )}
            />
            <Route render={() => <Redirect to={reportsHomePage} />} />
        </Switch>
    )
}

export default withFeatures(ReportsHome)
